import React from 'react';
import Drawer, { FooterSpacer } from '../Drawer';
import Button from '../Button';
import Field from '../Field';
import ScanNotice from './ScanNotice';
import AttachmentField from './AttachmentField';
import RecurrenceFields from './RecurrenceFields';
import {
  CATEGORY_UNSET_LABEL, EXPENSE_PAYEE_MAX, EXPENSE_DESCRIPTION_MAX,
} from '../../domain/expenses';
import { entryDrawerTitle, payeeLabel } from '../../domain/expenseForms';
import { currencySymbol } from '../../domain/money';
import { useCurrency, useLocale } from '../../context/LocaleContext';
import s from './EntryForm.module.css';
import { DateInput, DecimalInput } from '../RegionInputs';

// A date input will accept a five-digit year if digits are typed into a field
// that already holds one, which turns an indefinite monthly rule into an
// 83-year expansion. min/max makes the state unreachable.
const DATE_MIN = '1900-01-01';
const DATE_MAX = '2999-12-31';

export default function EntryFormDrawer({
  open, isRevenue, editing, form, onChange, categories,
  saving, scan, attaching, attachError,
  receiptUploads = [], onRetryReceipt = null, onCancelReceipt = null, onDismissReceipt = null,
  onBack, onClose, onCancel, onSave, onSaveAndAdd, canSave = true, errors = {},
  onAddCategory, onAttach, onDetach, onViewAttachment, onCopyAttachmentLink = null, attachmentNotice = null,
}) {
  const type = isRevenue ? 'revenue' : 'expense';
  const currency = useCurrency();
  const { locale } = useLocale();
  const symbol = currencySymbol(currency, locale);

  return (
    <Drawer
      open={open}
      // No back arrow when editing: an edit is reached from a row, not from the
      // picker, so there is nothing behind it to go back to.
      onBack={editing ? null : onBack}
      onClose={onClose}
      title={entryDrawerTitle(type, editing)}
      width={560}
      footer={(
        <>
          {editing ? null : (
            <Button variant="secondary" onClick={onSaveAndAdd} loading={saving === 'another'} disabled={!canSave}>
              Save &amp; Add Another
            </Button>
          )}
          <FooterSpacer />
          <Button variant="secondary" onClick={onCancel}>Cancel</Button>
          <Button onClick={onSave} loading={saving === 'save'} disabled={!canSave}>Save</Button>
        </>
      )}
    >
      <div className={s.body}>
        <ScanNotice scan={scan} />

        <div className={s.sectionTitle}>Details</div>

        {/* Required (#116), as MMS refuses an empty Payee inline on submit. No
            asterisk: MMS marks the optional fields ("Optional" under
            Description and Attachments) and leaves the required ones unmarked. */}
        <Field label={payeeLabel(type)} error={errors.payee}>
          {/* The client half of the 200-character bound firestore.rules puts on
              this field (#145), so a refusal is said on screen (#115). */}
          <input
            className={s.input}
            value={form.payee}
            maxLength={EXPENSE_PAYEE_MAX}
            data-error={errors.payee ? 'true' : undefined}
            onChange={(e) => onChange({ payee: e.target.value })}
          />
        </Field>

        <div className={s.section}>
          {/* Required, and "Not Specified" is the empty state rather than a
              choice, as in MMS: the select displays those words when nothing is
              picked, its dropdown does not offer them, and Save refuses the
              field as missing.

              `disabled hidden` is the HTML for exactly that: shown while it is
              the value, absent from the list once opened, and unreachable
              afterwards. Both attributes: `hidden` alone is unreliable in
              Safari, and `disabled` alone would leave a greyed row in the
              list. */}
          <Field label="Category" error={errors.category}>
            <select
              className={s.input}
              value={form.category}
              data-error={errors.category ? 'true' : undefined}
              onChange={(e) => onChange({ category: e.target.value })}
            >
              <option value="" disabled hidden>{CATEGORY_UNSET_LABEL}</option>
              {categories.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </Field>
          <button className={s.linkButton} onClick={onAddCategory}>+ Add Category</button>
        </div>

        <div className={s.section}>
          {/* The `htmlFor`/`id` pair by hand: the currency prefix means the
              input is inside a wrapper, and `Field` labels only a child it can
              legally name (#383). */}
          <Field label="Amount" htmlFor="expense-amount" error={errors.amount}>
            {/* The studio's sign, not a typed `$`. A sign can be three
                characters (`CHF`, `CA$`), so the input's left padding follows
                its length rather than fitting one glyph. */}
            <div className={s.amountWrap}>
              <span className={s.amountPrefix}>{symbol}</span>
              {/* The box is the Region's (`DecimalInput`, #526): `16,95` in
                  Germany. A third decimal is truncated one level up, by `patch`
                  in src/hooks/useExpenseEntryForm.js. */}
              <DecimalInput
                id="expense-amount"
                className={`${s.input} ${s.amountInput}`}
                style={{ paddingLeft: `calc(${symbol.length}ch + 18px)` }}
                value={form.amount}
                data-error={errors.amount ? 'true' : undefined}
                onChange={(e) => onChange({ amount: e.target.value })}
              />
            </div>
          </Field>
        </div>

        <label className={s.checkRow}>
          <input type="checkbox" checked={form.isRefund} onChange={(e) => onChange({ isRefund: e.target.checked })} />
          <span>This is a refund</span>
        </label>

        {/* Remove goes to the hook rather than to `onChange` (#94, #104): the
            hook owns the list because it also remembers the objects behind
            it. */}
        <AttachmentField
          attachments={form.attachments}
          busy={attaching}
          error={attachError}
          uploads={receiptUploads}
          onRetryUpload={onRetryReceipt}
          onCancelUpload={onCancelReceipt}
          onDismissUpload={onDismissReceipt}
          onPick={onAttach}
          onRemove={onDetach}
          onView={onViewAttachment}
          onCopyLink={onCopyAttachmentLink}
          notice={attachmentNotice}
        />

        <div className={s.section}>
          <Field label="Description" hint="Optional">
            <textarea rows={3} className={s.input} value={form.description}
                      maxLength={EXPENSE_DESCRIPTION_MAX}
                      onChange={(e) => onChange({ description: e.target.value })} />
          </Field>
        </div>

        {/* Required (#132), by the same interaction as the three above: the
            field goes red on Save, not on blur, and Save is never disabled for
            it. MMS captions the optional fields and leaves this one bare.
            `entryErrors` has the rest. */}
        <div className={s.section}>
          <Field label="Date" error={errors.occurredOn}>
            <DateInput className={s.input} value={form.occurredOn} min={DATE_MIN} max={DATE_MAX}
                   data-error={errors.occurredOn ? 'true' : undefined}
                   onChange={(e) => onChange({ occurredOn: e.target.value })} />
          </Field>
        </div>

        <RecurrenceFields form={form} onChange={onChange} type={type} error={errors.endDate} />
      </div>
    </Drawer>
  );
}
